import { ClaimResponse as Claim } from '@sentara-health/sentara-api-hooks-core';
import { IndexedClaim, buildIndexedClaims } from './IndexedClaimUtils';

export interface ClaimsIndex {
  byId: Map<string, Claim>;
  indexedById: Map<string, IndexedClaim>;
  byDateAsc: string[];
  byAmountAsc: string[];
  byDateDesc: string[];
  byAmountDesc: string[];
  byServiceType: Map<string, string[]>;
  byProvider: Map<string, string[]>;
  byLocation: Map<string, string[]>;
  byStatus: Map<string, string[]>;
  compound: {
    statusAndServiceType: Map<string, string[]>;
    providerAndStatus: Map<string, string[]>;
  };
  totalCount: number;
  builtAt: number;
}

function getClaimId(claim: Claim): string {
  const match = claim.identifier?.find(
    (id: any) =>
      id?.system?.toLowerCase()?.includes('claimid') ||
      id?.value
  );
  return match?.value ?? '';
}

function normalizeKey(value: unknown): string {
  const s = typeof value === 'string' ? value : '';
  return s.trim().toLowerCase();
}

function explodeTokens(raw?: string): string[] {
  if (!raw) return [];
  return raw
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
}

function makeCompoundKey(a: string, b: string): string {
  return `${normalizeKey(a)}|${normalizeKey(b)}`;
}

/** Add a single value to bucket map with normalization. */
function addToBucket(
  bucketMap: Map<string, string[]>,
  rawKey: string,
  id: string
): void {
  const key = normalizeKey(rawKey);
  if (!key) return;

  const existing = bucketMap.get(key);
  if (existing) {
    if (!existing.includes(id)) existing.push(id);
    return;
  }
  bucketMap.set(key, [id]);
}

/** Split tokens from a value and add each token to bucket  */
function addTokensToBucket(
  bucketMap: Map<string, string[]>,
  rawValue: string,
  id: string
): void {
  const tokens = explodeTokens(rawValue);
  for (const token of tokens) {
    addToBucket(bucketMap, token, id);
  }
}

export function buildClaimsIndex(rawClaims: Claim[]): ClaimsIndex {
  if (!Array.isArray(rawClaims) || !rawClaims.length) {
    return {
      byId: new Map(),
      indexedById: new Map(),
      byDateAsc: [],
      byAmountAsc: [],
      byDateDesc: [],
      byAmountDesc: [],
      byServiceType: new Map(),
      byProvider: new Map(),
      byLocation: new Map(),
      byStatus: new Map(),
      compound: {
        statusAndServiceType: new Map(),
        providerAndStatus: new Map(),
      },
      totalCount: 0,
      builtAt: Date.now(),
    };
  }

  const indexedClaims = buildIndexedClaims(rawClaims);

  const byId = new Map<string, Claim>();
  const indexedById = new Map<string, IndexedClaim>();

  const byServiceType = new Map<string, string[]>();
  const byProvider = new Map<string, string[]>();
  const byLocation = new Map<string, string[]>();
  const byStatus = new Map<string, string[]>();

  const statusAndServiceType = new Map<string, string[]>();
  const providerAndStatus = new Map<string, string[]>();

  const dateEntries: Array<{ id: string; date: number }> = [];
  const amountEntries: Array<{ id: string; amount: number }> = [];

  for (const indexed of indexedClaims) {
    const claimId = getClaimId(indexed.original);
    if (!claimId) continue;

    byId.set(claimId, indexed.original);
    indexedById.set(claimId, indexed);

    addTokensToBucket(byServiceType, indexed.serviceType, claimId);
    addToBucket(byProvider, indexed.providerName, claimId);
    addToBucket(byLocation, indexed.location, claimId);
    addToBucket(byStatus, indexed.status, claimId);

    const serviceTypeTokens = explodeTokens(indexed.serviceType);
    for (const typeToken of serviceTypeTokens) {
      addToBucket(
        statusAndServiceType,
        makeCompoundKey(indexed.status, typeToken),
        claimId
      );
    }

    addToBucket(
      providerAndStatus,
      makeCompoundKey(indexed.providerName, indexed.status),
      claimId
    );

    dateEntries.push({
      id: claimId,
      date: Number(indexed.dateOfService),
    });

    amountEntries.push({
      id: claimId,
      amount: Number(indexed.amountOwed),
    });
  }

  dateEntries.sort((a, b) => a.date - b.date);
  const byDateAsc = dateEntries.map((d) => d.id);
  const byDateDesc = [...byDateAsc].reverse();

  amountEntries.sort((a, b) => a.amount - b.amount);
  const byAmountAsc = amountEntries.map((a) => a.id);
  const byAmountDesc = [...byAmountAsc].reverse();

  return {
    byId,
    indexedById,
    byDateAsc,
    byAmountAsc,
    byDateDesc,
    byAmountDesc,
    byServiceType,
    byProvider,
    byLocation,
    byStatus,
    compound: {
      statusAndServiceType,
      providerAndStatus,
    },
    totalCount: byId.size,
    builtAt: Date.now(),
  };
}

export function getClaimById(
  index: ClaimsIndex,
  id: string
): Claim | undefined {
  return index.byId.get(id);
}

export function getIndexedClaimById(
  index: ClaimsIndex,
  id: string
): IndexedClaim | undefined {
  return index.indexedById.get(id);
}

export function getBucketIds(
  bucket: Map<string, string[]>,
  key: string
): string[] {
  return bucket.get(normalizeKey(key)) ?? [];
}